import { useCallback, useMemo, useRef, useState } from 'react'
import { useIntl } from 'react-intl'
import { SelectButton } from 'primereact/selectbutton'
import {
  HospitalizationDailyPointDTO,
  HospitalizationFilterDTO,
  HospitalizationSummaryDTO,
} from '../../../dto/hospitalizationSummary/HospitalizationSummaryDTO'
import {
  averagePerDay,
  HOSPITALIZATION_MEASURES,
  HospitalizationMeasure,
  occupancyPeaks,
  weightedOccupancy,
} from '../hospitalizationEvolutionModel'
import HospitalizationDaySidebar from './HospitalizationDaySidebar'
import HospitalizationEvolutionChart from './HospitalizationEvolutionChart'
import HospitalizationPeakChips from './HospitalizationPeakChips'

const MEASURE_LABEL_ID: Record<HospitalizationMeasure, string> = {
  occupancy: 'HospitalizationSummaryKpiOccupancy',
  admissions: 'HospitalizationSummaryKpiAdmissions',
  discharges: 'HospitalizationSummaryKpiDischarges',
}

interface HospitalizationEvolutionProps {
  summary: HospitalizationSummaryDTO
  /** Período y filtros aplicados de la pantalla. */
  filters: HospitalizationFilterDTO
  /** La medida elegida vive en la pantalla: no se pierde al cambiar de pestaña ni de filtros. */
  measure: HospitalizationMeasure
  onMeasureChange: (measure: HospitalizationMeasure) => void
  /** «Ver solo este día» del panel: aplica la fecha como período de la pantalla. */
  onViewDay: (date: string) => void
}

/**
 * Pestaña «Evolución»: la ocupación, los ingresos o los egresos día por día, con la línea de promedio del período, los picos
 * de la ocupación (chips «Picos» junto al gráfico) y, al tocar un día, el panel lateral con su detalle. «Ocupación | Ingresos |
 * Egresos» son el mismo dato en otra vista (SelectButton): no llevan rótulo visible, solo nombre accesible. Todo sale de la
 * serie diaria del resumen, así que no hay consulta propia; el panel del día sí consulta (`/summary` con desde = hasta).
 */
export default function HospitalizationEvolution({ summary, filters, measure, onMeasureChange, onViewDay }: HospitalizationEvolutionProps) {
  const intl = useIntl()
  const days = summary.daily

  const analysis = useMemo(() => (measure === 'occupancy' ? occupancyPeaks(days) : null), [days, measure])
  const average = useMemo(() => {
    if (measure === 'occupancy') return summary.current.occupancyPct ?? weightedOccupancy(days)
    return averagePerDay(days, measure)
  }, [days, measure, summary.current.occupancyPct])

  // ── Panel del día ──
  const [detail, setDetail] = useState<HospitalizationDailyPointDTO | null>(null)
  const openerRef = useRef<HTMLElement | null>(null)
  /** Abre el panel de un día; al cerrarlo el foco vuelve a `opener` (el gráfico o el chip). */
  const openDay = useCallback((day: HospitalizationDailyPointDTO, opener: HTMLElement | null) => {
    openerRef.current = opener
    setDetail(day)
  }, [])
  const closeDetail = useCallback(() => {
    setDetail(null)
    const opener = openerRef.current
    openerRef.current = null
    // Cuando el panel ya se cerró; si el gráfico o el chip ya no están, no hay a dónde volver
    if (opener) window.setTimeout(() => { if (opener.isConnected) opener.focus() }, 0)
  }, [])
  const viewDay = useCallback((date: string) => {
    onViewDay(date)
    closeDetail()
  }, [onViewDay, closeDetail])

  const measureOptions = HOSPITALIZATION_MEASURES.map((value) => ({ value, label: intl.formatMessage({ id: MEASURE_LABEL_ID[value] }) }))
  const chartLabel = `${intl.formatMessage({ id: 'HospitalizationSummaryEvolutionLabel' })} · ${intl.formatMessage({ id: MEASURE_LABEL_ID[measure] })}`

  return (
    <section className="nv-hosp-evolution" aria-label={intl.formatMessage({ id: 'HospitalizationSummaryTabEvolution' })}>
      <div className="nv-hosp-evolution__controls">
        <SelectButton
          value={measure}
          options={measureOptions}
          optionLabel="label"
          optionValue="value"
          onChange={(event) => { if (event.value) onMeasureChange(event.value as HospitalizationMeasure) }}
          aria-label={intl.formatMessage({ id: 'HospitalizationSummaryMeasureBy' })}
        />
      </div>

      {days.length === 0
        ? <p className="nv-muted">{intl.formatMessage({ id: 'HospitalizationSummaryEmpty' })}</p>
        : (
          <div className="nv-hosp-evolution__content">
            <HospitalizationEvolutionChart
              days={days}
              measure={measure}
              ariaLabel={chartLabel}
              average={average}
              analysis={analysis}
              onOpen={openDay}
            />
            {analysis && <HospitalizationPeakChips days={days} analysis={analysis} onOpen={openDay} />}
          </div>
        )}

      <HospitalizationDaySidebar day={detail} filters={filters} onViewDay={viewDay} onHide={closeDetail} />
    </section>
  )
}
